import { SeriesOptionsType } from "highcharts";
import { ChartAxisProps, ChartDataSeries, ChartType } from "../../../components/Chart";
import { ReportingPeriods } from "../../../types";
import { getMonthNameAbbreviated } from "../../../utils/monthNames";
import ApiClient from "../../api";

export type LeverageTrendChartData = {
  series: Array<ChartDataSeries>;
  xAxis: ChartAxisProps;
  yAxis: ChartAxisProps;
};

export type LeverageTrendDBData = {
  covenant: {
    dataSeries: Array<LeverageTrendDetail>;
  };
  gross: {
    dataSeries: Array<LeverageTrendDetail>;
  };
  net: {
    dataSeries: Array<LeverageTrendDetail>;
  };
};

export type LeverageTrendDetail = {
  financialYear: number;
  quarter: number;
  month: number;
  year: number;
  value: number;
};

export const getLeverageTrendChartData = async (
  companyId: number,
  reportingCycle: ReportingPeriods,
  financialYear: number,
  enableConversions: boolean = false,
  unit: string = ""
) => {
  const queryString = `?type=${
    ChartType.LEVERAGE_TREND
  }&companyId=${companyId}&reportingCycle=${reportingCycle}&financialYear=${financialYear}&enableConversions=${
    enableConversions ? "true" : "false"
  }&unit=${unit ? unit : ""}`;

  const seriesFromDB = await ApiClient.get(`/charts${queryString}`);

  if (!seriesFromDB.data) return null;

  let xAxis: ChartAxisProps | null = null;
  const xAxisTitles = await getXAxisTitles(seriesFromDB.data, reportingCycle);

  if (xAxisTitles) {
    xAxis = {
      titles: xAxisTitles,
      colors: [],
    };
  }

  const yAxis: ChartAxisProps = {
    titles: [],
    colors: [],
  };

  const series = await getSeries(seriesFromDB.data, reportingCycle);

  return {
    series,
    xAxis,
    yAxis,
  } as LeverageTrendChartData;
};

const getXAxisTitles = async (data: LeverageTrendDBData, reportingCycle: ReportingPeriods) => {
  let detailToUse: LeverageTrendDetail[];

  // using the first arrays with data
  if (data.covenant.dataSeries?.length > 0) {
    detailToUse = data.covenant.dataSeries;
  } else if (data.net.dataSeries?.length > 0) {
    detailToUse = data.net.dataSeries;
  } else if (data.gross.dataSeries?.length > 0) {
    detailToUse = data.gross.dataSeries;
  } else {
    return [];
  }

  if (reportingCycle === ReportingPeriods.Quarterly) {
    let dataToReturn = [];
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)} ${detailToUse[detail].year}`);
    }
    return dataToReturn;
  } else if (reportingCycle === ReportingPeriods.Monthly) {
    let dataToReturn = [];
    for (const detail in detailToUse) {
      dataToReturn.push(`${await getMonthNameAbbreviated(detailToUse[detail].month)} ${detailToUse[detail].year}`);
    }
    return dataToReturn;
  } else {
    return [];
  }
};

// Use getSeries to decorate the series with specific elements to the chart.
const getSeries = async (data: LeverageTrendDBData, reportingCycle: ReportingPeriods) => {
  const series: SeriesOptionsType[] = [
    {
      animation: false,
      name: "Net Leverage Covenant",
      data: await getRawDataFromSeries(data.covenant.dataSeries, reportingCycle),
      color: "#afe2ff",
      type: "column",
      yAxis: 1,
      borderRadius: 0,
    },
    {
      animation: false,
      name: "Gross Total Leverage",
      data: await getRawDataFromSeries(data.gross.dataSeries, reportingCycle),
      color: "#afe2ff",
      type: "line",
      yAxis: 1,
    },
    {
      animation: false,
      name: "Net Total Leverage",
      data: await getRawDataFromSeries(data.net.dataSeries, reportingCycle),
      color: "#4d768c",
      type: "line",
      dashStyle: "Dash",
      yAxis: 1,
    },
  ];
  return series;
};

const getRawDataFromSeries = async (data: Array<LeverageTrendDetail>, reportingCycle: ReportingPeriods) => {
  if (!data || data?.length === 0) return [];

  // switch (reportingCycle) {
  //   case ReportingPeriods.Quarterly:
  //     return [data[0].value, data[1].value, data[2].value, data[3].value];
  //   case ReportingPeriods.Monthly:
  //     return [data[8].value, data[9].value, data[10].value, data[11].value];
  //   default:
  //     return [];
  // }
  let rawDataToReturn = [];

  switch (reportingCycle) {
    case ReportingPeriods.Quarterly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    case ReportingPeriods.Monthly:
      for (const rawData in data) {
        rawDataToReturn.push(data[rawData].value);
      }
      return rawDataToReturn;
    default:
      return [];
  }
};
